import React, { useCallback, useMemo, useState } from 'react';

import { AsaphButton } from '@/components/shared/Button/AsaphButton';
import { Loader } from '@/components/shared/Loader';
import { SheetLayout } from '@/components/shared/SheetLayout';
import { Tabs } from '@/components/shared/Tabs';
import { useGateAccepted } from '@/components/shared/UpgradeGateSheet';
import {
	ListMyTeamSetlistsForReportsQuery,
	useListMyTeamSetlistsForReportsQuery,
} from '@/graphql';
import {
	birthYearToGeneration,
	GENERATIONS,
} from '@/helpers/reportsDataHelpers';
import { useFormattedTitle } from '@/hooks/useFormattedTitle';
import { ReportsDateRange, ReportsScope } from '@/hooks/useReportsAccess';
import { useReportsData } from '@/hooks/useReportsData';
import { useFireAnalyticsPageView, useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { useAuth } from '@/providers/AuthProvider';
import { useServices } from '@/providers/ServicesProvider';
import { useSubscription } from '@/providers/SubscriptionProvider';
import { useTeam } from '@/providers/TeamProvider';
import constants from '@/theme/exportedVariables.module.scss';
import { isPlatform } from '@ionic/react';
import { useMediaQuery } from '@material-ui/core';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router';

import { AverageSentimentCard } from './components/AverageSentimentCard/AverageSentimentCard';
import { DateRangeButton } from './components/DateRangeButton/DateRangeButton';
import { DistributionCirclePackingChart } from './components/DistributionCirclePackingChart/DistributionCirclePackingChart';
import { FeedbackRadarChart } from './components/FeedbackRadarChart/FeedbackRadarChart';
import { ReportsDataGate } from './components/ReportsDataGate/ReportsDataGate';
import { ReportsFilterBar } from './components/ReportsFilterBar/ReportsFilterBar';
import { SentimentHistoryChart } from './components/SentimentHistoryChart/SentimentHistoryChart';
import { TopTenSection } from './components/TopTenSection/TopTenSection';
import { TrendLineChart } from './components/TrendLineChart/TrendLineChart';
import styles from './Reports.module.scss';

export const Reports: React.FC = () => {
	const [title] = useFormattedTitle('Asaph | Reports');
	const isDesktop = useMediaQuery(
		`(min-width: ${constants.sizeSmallDesktop})`,
	);
	const isMobileDevice = isPlatform('mobile');
	const navigate = useNavigate();

	const { user } = useAuth();

	const [tab, setTab] = useState<'songs' | 'feedback'>('songs');
	const [dateRange, setDateRange] = useState<ReportsDateRange>('last-30');
	const gateAccepted = useGateAccepted('reports_date_range');
	const { status: subscriptionStatus } = useSubscription();
	const isGated =
		(dateRange === 'last-90' || dateRange === 'year') && !gateAccepted && !subscriptionStatus;
	const [skillFilter, setSkillFilter] = useState<string[]>([]);
	const [genderFilter, setGenderFilter] = useState<string[]>([]);
	const [generationFilter, setGenerationFilter] = useState<string[]>([]);
	const [serviceFilter, setServiceFilter] = useState<string[]>([]);
	const [selectedYear, setSelectedYear] = useState<number>(
		new Date().getFullYear(),
	);

	// Both tabs use team scope — song content charts reflect what the whole team
	// has been performing, not just the logged-in user's submissions.
	const scope: ReportsScope = 'team';

	const fireTabChanged = useAnalyticsEvent('Reports_Tab_Changed');
	const fireSkillChanged = useAnalyticsEvent('Reports_Filter_Skill_Changed');
	const fireGenderChanged = useAnalyticsEvent('Reports_Filter_Gender_Changed');
	const fireGenerationChanged = useAnalyticsEvent('Reports_Filter_Generation_Changed');
	const fireServiceChanged = useAnalyticsEvent('Reports_Filter_Service_Changed');
	const fireFiltersCleared = useAnalyticsEvent('Reports_Filters_Cleared');
	const fireDateRangeChanged = useAnalyticsEvent('Reports_Date_Range_Changed');
	const fireYearSelected = useAnalyticsEvent('Reports_Year_Selected');

	const handleSkillToggle = useCallback((value: string) => {
		setSkillFilter((prev) =>
			prev.includes(value)
				? prev.filter((v) => v !== value)
				: [...prev, value],
		);
		fireSkillChanged({ value });
	}, [fireSkillChanged]);

	const handleGenderToggle = useCallback((value: string) => {
		setGenderFilter((prev) =>
			prev.includes(value)
				? prev.filter((v) => v !== value)
				: [...prev, value],
		);
		fireGenderChanged({ value });
	}, [fireGenderChanged]);

	const handleGenerationToggle = useCallback((value: string) => {
		setGenerationFilter((prev) =>
			prev.includes(value)
				? prev.filter((v) => v !== value)
				: [...prev, value],
		);
		fireGenerationChanged({ value });
	}, [fireGenerationChanged]);

	const handleServiceToggle = useCallback((value: string) => {
		setServiceFilter((prev) =>
			prev.includes(value)
				? prev.filter((v) => v !== value)
				: [...prev, value],
		);
		fireServiceChanged({ value });
	}, [fireServiceChanged]);

	const handleClearAll = useCallback(() => {
		setSkillFilter([]);
		setGenderFilter([]);
		setGenerationFilter([]);
		setServiceFilter([]);
		fireFiltersCleared();
	}, [fireFiltersCleared]);

	const handleTabChange = useCallback((index: number) => {
		const nextTab = index === 0 ? 'songs' : 'feedback';
		setTab(nextTab);
		fireTabChanged({ tab: nextTab });
		// Demographic/skill filters only apply to the Feedback tab; clear them on switch
		if (index === 0) {
			setSkillFilter([]);
			setGenderFilter([]);
			setGenerationFilter([]);
		}
	}, [fireTabChanged]);

	const handleDateRangeChange = useCallback((value: ReportsDateRange) => {
		setDateRange(value);
		fireDateRangeChanged({ range: value });
	}, [fireDateRangeChanged]);

	const handleYearChange = useCallback((year: number) => {
		setSelectedYear(year);
		fireYearSelected({ year });
	}, [fireYearSelected]);

	const { team } = useTeam();
	const isAdmin = useMemo(
		() =>
			team?.members
				.find((m) => m.user.id === user?.id)
				?.roles.includes('teamAdmin') ?? false,
		[team, user],
	);
	const { services } = useServices();

	const { data: reportsSetlistsData, isLoading: reportsSetlistsLoading } =
		useListMyTeamSetlistsForReportsQuery<
			ListMyTeamSetlistsForReportsQuery,
			Error
		>({}, { refetchOnWindowFocus: false });

	const setlists = useMemo(
		() => reportsSetlistsData?.listMyTeamSetlists ?? [],
		[reportsSetlistsData],
	);

	const skillOptions = useMemo(() => {
		const skillSet = new Set<string>();
		team.members.forEach((m) =>
			(m.user.skills ?? []).forEach((s) => skillSet.add(s)),
		);
		return Array.from(skillSet)
			.sort()
			.map((s) => ({
				label: s.charAt(0).toUpperCase() + s.slice(1),
				value: s,
			}));
	}, [team.members]);

	const genderOptions = useMemo(() => {
		const genderSet = new Set<string>();
		team.members.forEach((m) => {
			if (m.user.gender) genderSet.add(m.user.gender);
		});
		return Array.from(genderSet)
			.sort()
			.map((g) => ({
				label: g.charAt(0).toUpperCase() + g.slice(1),
				value: g,
			}));
	}, [team.members]);

	const generationOptions = useMemo(() => {
		const genSet = new Set<string>();
		team.members.forEach((m) =>
			genSet.add(birthYearToGeneration(m.user.birthYear)),
		);
		// Preserve canonical GENERATIONS order, only include generations present in the team
		return GENERATIONS.filter(
			(gen) => gen !== 'Unknown' && genSet.has(gen),
		).map((gen) => ({ label: gen, value: gen }));
	}, [team.members]);

	const serviceOptions = useMemo(
		() => services.map((s) => ({ label: s.name, value: s.id })),
		[services],
	);

	const availableYears = useMemo(() => {
		const yearSet = new Set<number>();
		setlists.forEach((s) => {
			if (!s.draft) yearSet.add(new Date(s.startAt).getFullYear());
		});
		return Array.from(yearSet).sort((a, b) => b - a); // descending: newest first
	}, [setlists]);

	const reportsData = useReportsData({
		scope,
		dateRange,
		skillFilter,
		genderFilter,
		generationFilter,
		serviceFilter,
		selectedYear,
		setlistsOverride: setlists,
	});

	useFireAnalyticsPageView({ enabled: !!title, pageViewArgs: { title } });

	return (
		<>
			<Helmet title={title} />
			<SheetLayout
				titleClassName={
					isDesktop ? styles['title-full-width'] : undefined
				}
				titleComponent={
					isDesktop ? (
						<div className={styles['title-row']}>
							<span>Reports</span>
							<DateRangeButton
								value={dateRange}
								onChange={handleDateRangeChange}
								availableYears={availableYears}
								selectedYear={selectedYear}
								onYearChange={handleYearChange}
							/>
						</div>
					) : (
						<span>Reports</span>
					)
				}
				topComponent={
					isDesktop ? (
						<div className={styles['top-component']}>
							<Tabs
								initialSelectedIndex={0}
								items={[
									{ title: 'Songs' },
									{ title: 'Feedback' },
								]}
								onChange={handleTabChange}
								className={styles['tabs-no-margin']}
							/>
							<ReportsFilterBar
								skillFilter={skillFilter}
								onSkillToggle={handleSkillToggle}
								skillOptions={
									tab === 'feedback'
										? skillOptions
										: undefined
								}
								genderFilter={genderFilter}
								onGenderToggle={handleGenderToggle}
								genderOptions={
									tab === 'feedback'
										? genderOptions
										: undefined
								}
								generationFilter={generationFilter}
								onGenerationToggle={handleGenerationToggle}
								generationOptions={
									tab === 'feedback'
										? generationOptions
										: undefined
								}
								serviceFilter={serviceFilter}
								onServiceToggle={handleServiceToggle}
								serviceOptions={
									services.length > 1
										? serviceOptions
										: undefined
								}
								onClearAll={handleClearAll}
							/>
						</div>
					) : undefined
				}
			>
				{reportsSetlistsLoading ? (
					<Loader fullText="Loading reports. This might take up to a minute" />
				) : (
					<>
						{!isDesktop ? (
							<div className={styles['desktop-only']}>
								<p className={styles['desktop-only__message']}>
									Reports are best viewed on a larger screen.
								</p>
								{isMobileDevice ? (
									<AsaphButton
										color="grey"
										label="Go back"
										onClick={() => navigate(-1)}
									/>
								) : (
									<p className={styles['desktop-only__hint']}>
										Try resizing your browser window.
									</p>
								)}
							</div>
						) : (
							<ReportsDataGate
								isGated={isGated}
								isAdmin={isAdmin}
							>
								<div className={styles['charts-list']}>
									{tab === 'songs' ? (
										<>
											<div
												className={styles['chart-pair']}
											>
												<DistributionCirclePackingChart
													title="Themes"
													section={
														reportsData.themeDistribution
													}
													defaultView="bubble"
												/>
												<DistributionCirclePackingChart
													title="Theology"
													section={
														reportsData.theologyDistribution
													}
													defaultView="word-cloud"
												/>
											</div>
											<div
												className={styles['chart-pair']}
											>
												<DistributionCirclePackingChart
													title="Gospel Story"
													section={
														reportsData.gospelStoryTrend
													}
													perSetlist={true}
													semanticColors
													defaultView="rank"
												/>
												<DistributionCirclePackingChart
													title="Emotions"
													section={
														reportsData.emotionTrend
													}
													perSetlist={true}
													semanticColors
												/>
											</div>
											<TrendLineChart
												title="Theme Trends"
												section={reportsData.themeTrend}
											/>
											<TrendLineChart
												title="Theology Trends"
												section={
													reportsData.theologyTrend
												}
											/>
											<TrendLineChart
												title="Gospel Story Trends"
												section={
													reportsData.gospelStoryTrend
												}
												semanticColors
											/>
											<TrendLineChart
												title="Emotion Trends"
												section={
													reportsData.emotionTrend
												}
												semanticColors
											/>
										</>
									) : (
										<>
											<TopTenSection
												section={reportsData.topTen}
											/>
											<SentimentHistoryChart
												section={
													reportsData.sentimentHistory
												}
											/>
											<AverageSentimentCard
												section={
													reportsData.averageSentiment
												}
											/>
											<div className={styles['chart-pair']}>
												<FeedbackRadarChart
													title="Service Feedback"
													section={
														reportsData.radarByServiceType
													}
												/>
												<FeedbackRadarChart
													title="Generation Feedback"
													section={
														reportsData.radarByGeneration
													}
												/>
											</div>
											<div className={styles['chart-pair']}>
												<FeedbackRadarChart
													title="Gender Feedback"
													section={
														reportsData.radarByGender
													}
												/>
												<FeedbackRadarChart
													title="Skill Feedback"
													section={
														reportsData.radarBySkill
													}
												/>
											</div>
										</>
									)}
								</div>
							</ReportsDataGate>
						)}
					</>
				)}
			</SheetLayout>
		</>
	);
};
